/**
 * UranIA PA — Topbar globale visibile su tutte le pagine post-login
 * tranne ``/login`` e il workspace Mod5 (che ha il proprio `Mod5Topbar`
 * dedicato con tenant selector + striscia identità ente).
 *
 * Espone:
 *   - Brand "UranIA PA" linkato a ``/dashboard``;
 *   - Badge tenant ULID troncato (idem Mod5Topbar);
 *   - Menu utente con avatar iniziali + dropdown "Esci"
 *     → ``startLogout()`` (oidc-client-ts + end_session_endpoint
 *     Keycloak + svuotamento Zustand store).
 *
 * Boundary:
 *   - Renderizzato dal ``__root.tsx`` solo se ``isAuthenticated`` e
 *     se la rotta non e' in ``HIDE_TOPBAR_PATHS``. Niente logica auth
 *     guard: la pagina protetta ha il proprio fallback per anonymous.
 */

import { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { selectPrincipal, useAuthStore } from '../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../lib/auth/rbac.ts';
import { startLogout } from '../lib/auth/login-actions.ts';

export function AppTopbar(): JSX.Element {
  const { t } = useTranslation();
  const principal = useAuthStore(selectPrincipal);
  const displayName = principal?.preferredUsername ?? principal?.subject ?? '';
  const tenantId = principal?.tenantId ?? null;
  const isPlatformAdmin = principalIsPlatformAdmin(principal);
  const tenantShort =
    tenantId !== null && tenantId.length > 12
      ? `${tenantId.substring(0, 6)}…${tenantId.substring(tenantId.length - 4)}`
      : tenantId;

  const userMenuId = useId();
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const toggleUserMenu = useCallback((): void => {
    setUserMenuOpen((open) => !open);
  }, []);

  const handleLogout = useCallback((): void => {
    setUserMenuOpen(false);
    void startLogout();
  }, []);

  return (
    <header
      className="urania-app-topbar bg-white border-bottom"
      role="banner"
      data-testid="app-topbar"
    >
      <div className="container-fluid d-flex align-items-center justify-content-between py-2 gap-3">
        <Link
          to="/dashboard"
          className="urania-app-topbar__brand d-flex align-items-baseline gap-2 text-decoration-none"
          data-testid="app-topbar-brand"
          aria-label={t('app_topbar.brand_home_aria')}
          title={t('app_topbar.brand_home_aria')}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 16 16"
            fill="#06c"
            aria-hidden="true"
          >
            <path d="M4 0h5.5L14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm5 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5H9.5A1.5 1.5 0 0 1 8 3.5V1zM3 9a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 9zm0 2.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
          </svg>
          <span className="h6 mb-0 fw-bold text-dark">UranIA PA</span>
        </Link>
        <style>
          {`
            .urania-app-topbar__brand { transition: opacity 120ms ease; cursor: pointer; }
            .urania-app-topbar__brand:hover { opacity: 0.7; }
            .urania-app-topbar__brand:hover span { text-decoration: underline; text-underline-offset: 4px; }
            .urania-app-topbar__brand:focus-visible { outline: 2px solid #0066cc; outline-offset: 4px; border-radius: 2px; }
          `}
        </style>

        <div className="d-flex align-items-center gap-2">
          {!isPlatformAdmin && tenantShort !== null && (
            <span
              className="badge rounded-pill bg-light text-dark border font-monospace small d-none d-md-inline"
              title={tenantId ?? ''}
              data-testid="app-topbar-tenant"
            >
              Tenant: {tenantShort}
            </span>
          )}

          <div className="position-relative" ref={userMenuRef}>
            {displayName.length === 0 ? (
              <Link
                to="/accedi"
                className="btn btn-sm btn-outline-primary"
                data-testid="app-topbar-login"
              >
                {t('app_topbar.login')}
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  aria-controls={userMenuId}
                  aria-label={t('app_topbar.user_menu_open_aria')}
                  data-testid="app-topbar-user-button"
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                    style={{ width: 24, height: 24, fontSize: 12, fontWeight: 600 }}
                    aria-hidden="true"
                  >
                    {displayName.substring(0, 2).toUpperCase()}
                  </span>
                  <span className="d-none d-sm-inline">{displayName}</span>
                  <span aria-hidden="true">▾</span>
                </button>
                <div
                  id={userMenuId}
                  role="menu"
                  aria-label={t('app_topbar.user_menu_label')}
                  hidden={!isUserMenuOpen}
                  className="card position-absolute end-0 mt-1 shadow"
                  style={{ minWidth: 240, zIndex: 1050 }}
                  data-testid="app-topbar-user-dropdown"
                >
                  <div className="card-body py-2 px-3 small">
                    <p className="mb-1">
                      {t('app_topbar.signed_in_as', { name: displayName })}
                    </p>
                    {!isPlatformAdmin && principal?.acr !== null && principal?.acr !== undefined && (
                      <p className="mb-2 text-muted">
                        {t('app_topbar.acr', { acr: principal.acr })}
                      </p>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className="btn btn-sm btn-outline-danger w-100"
                      onClick={handleLogout}
                      data-testid="app-topbar-logout"
                    >
                      {t('app_topbar.logout')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
